/**
 * Hooks for copy log operations — already-copied checks, history, and copy mutations.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { COPY_QUERY_KEYS } from "./useSourceEntities";
import type {
  AlreadyCopiedSet,
  CopyEntityType,
  CopyHistoryEntry,
  CopyOperationResult,
} from "../types";

export function useAlreadyCopied(
  destPortalId: string | null | undefined,
  sourcePortalId: string | null | undefined,
  entityType: CopyEntityType,
) {
  return useQuery({
    queryKey: COPY_QUERY_KEYS.alreadyCopied(
      destPortalId ?? "",
      sourcePortalId ?? "",
      entityType,
    ),
    queryFn: async () => {
      const resp = await apiClient.get<AlreadyCopiedSet>(
        API_ENDPOINTS.SYSTEM.PORTALS.IMPORT.ALREADY_COPIED(destPortalId!),
        { params: { source_portal_id: sourcePortalId, entity_type: entityType } },
      );
      return resp.data;
    },
    enabled: !!destPortalId && !!sourcePortalId,
  });
}

export function useCopyHistory(destPortalId: string | null | undefined) {
  return useQuery({
    queryKey: COPY_QUERY_KEYS.history(destPortalId ?? ""),
    queryFn: async () => {
      const resp = await apiClient.get<{
        items: CopyHistoryEntry[];
        total: number;
      }>(API_ENDPOINTS.SYSTEM.PORTALS.IMPORT.HISTORY(destPortalId!));
      return resp.data;
    },
    enabled: !!destPortalId,
  });
}

interface CopyVariables {
  destPortalId: string;
  sourcePortalId: string;
  items: Array<{ id: string; name: string }>;
}

function useCopyMutation(entityType: CopyEntityType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: CopyVariables): Promise<CopyOperationResult> => {
      const ids = vars.items.map((i) => i.id);

      // Map entity type to correct endpoint + request body field
      let url: string;
      let body: Record<string, unknown>;

      if (entityType === "agent") {
        url = API_ENDPOINTS.SYSTEM.PORTALS.IMPORT.AGENTS(vars.destPortalId);
        body = {
          source_portal_id: vars.sourcePortalId,
          source_agent_ids: ids,
        };
      } else if (entityType === "cms_page") {
        url = API_ENDPOINTS.SYSTEM.PORTALS.IMPORT.CMS_PAGES(vars.destPortalId);
        body = {
          source_portal_id: vars.sourcePortalId,
          source_page_ids: ids,
        };
      } else if (entityType === "llm_model") {
        url = API_ENDPOINTS.SYSTEM.PORTALS.IMPORT.LLM_MODELS(vars.destPortalId);
        body = {
          source_portal_id: vars.sourcePortalId,
          source_model_ids: ids,
        };
      } else {
        url = API_ENDPOINTS.SYSTEM.PORTALS.IMPORT.FILES(vars.destPortalId);
        body = {
          source_portal_id: vars.sourcePortalId,
          source_file_ids: ids,
        };
      }

      const resp = await apiClient.post<CopyOperationResult>(url, body);
      return resp.data;
    },
    onSuccess: (_result, vars) => {
      queryClient.invalidateQueries({
        queryKey: COPY_QUERY_KEYS.alreadyCopied(
          vars.destPortalId,
          vars.sourcePortalId,
          entityType,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: COPY_QUERY_KEYS.history(vars.destPortalId),
      });
    },
  });
}

export const useCopyAgents = () => useCopyMutation("agent");
export const useCopyCmsPages = () => useCopyMutation("cms_page");
export const useCopyFiles = () => useCopyMutation("file");
export const useCopyLlmModels = () => useCopyMutation("llm_model");
